import React, { useState } from "react";
import {
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
} from "@fluentui/react-tag-picker";
import { Skeleton, SkeletonItem, Tag, makeStyles } from "@fluentui/react-components";
import {
  tagPickerListPositioning,
  useTagPickerOptionLayoutStyles,
} from "./TagPickerOptionLayout";

const useStyles = makeStyles({
  list: {
    maxHeight: "300px",
    overflowY: "auto",
  },
});

export interface SourceSiteTagPickerOption {
  id: string;
  label: string;
  secondaryLabel?: string;
}

interface SourceSiteTagPickerProps {
  selectedSiteId?: string;
  selectedSiteLabel?: string;
  query: string;
  placeholder: string;
  options: SourceSiteTagPickerOption[];
  onQueryChange: (value: string) => void;
  onSelectSite: (siteId?: string) => void;
  minQueryLength?: number;
  typeToSearchText: string;
  clearSelectionText: string;
  isLoading?: boolean;
}

export const SourceSiteTagPicker: React.FC<SourceSiteTagPickerProps> = ({
  selectedSiteId,
  selectedSiteLabel,
  query,
  placeholder,
  options,
  onQueryChange,
  onSelectSite,
  minQueryLength = 2,
  typeToSearchText,
  clearSelectionText,
  isLoading,
}) => {
  const styles = useStyles();
  const optionLayout = useTagPickerOptionLayoutStyles();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= minQueryLength;
  const selectedOptions = selectedSiteId ? [selectedSiteId] : [];
  const clearSelectionValue = "__clearSelection";
  const minQueryInfoValue = "__minQueryLength";

  const availableOptions = options.filter(
    (option) => option.id !== selectedSiteId,
  );

  // The skeleton is for the first load only. Every search sets isLoading, and swapping the
  // picker out then unmounted the input: the list closed and reopened only on ArrowDown.
  if (isLoading && !isInputFocused && trimmedQuery === "") {
    return (
      <Skeleton>
        <SkeletonItem shape="rectangle" size={32} />
      </Skeleton>
    );
  }

  return (
    <TagPicker
      positioning={tagPickerListPositioning}
      selectedOptions={selectedOptions}
      onOptionSelect={(_, data) => {
        // The "type to search" row is a hint, not an option: it selects
        // nothing, so the term the user is still typing has to survive it.
        if (data.value === minQueryInfoValue) {
          return;
        }

        // Picking an option ends the search, so the term goes with it.
        onQueryChange("");

        if (data.value === clearSelectionValue) {
          onSelectSite(undefined);
          return;
        }
        onSelectSite(data.value);
      }}
    >
      <TagPickerControl>
        <TagPickerGroup>
          {/* While a new search is typed, the selection it may replace is hidden. */}
          {selectedSiteId && selectedSiteLabel && query === "" ? (
            <Tag value={selectedSiteId}>{selectedSiteLabel}</Tag>
          ) : null}
        </TagPickerGroup>
        <TagPickerInput
          value={query}
          placeholder={placeholder}
          onChange={(event) => {
            onQueryChange(event.currentTarget.value);
          }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />
      </TagPickerControl>
      <TagPickerList className={styles.list}>
        {selectedSiteId ? (
          <TagPickerOption
            text={clearSelectionText}
            value={clearSelectionValue}
          >
            {clearSelectionText}
          </TagPickerOption>
        ) : null}
        {!canSearch ? (
          <TagPickerOption text={typeToSearchText} value={minQueryInfoValue}>
            {typeToSearchText}
          </TagPickerOption>
        ) : (
          availableOptions.map((option) => (
            <TagPickerOption
              key={option.id}
              value={option.id}
              text={option.label}
              className={optionLayout.option}
              secondaryContent={
                option.secondaryLabel
                  ? { children: option.secondaryLabel, className: optionLayout.secondaryContent }
                  : undefined
              }
            >
              {option.label}
            </TagPickerOption>
          ))
        )}
      </TagPickerList>
    </TagPicker>
  );
};
